import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'

/**
 * Per-currency FX rate + markup + low-balance threshold. A credit is 1 unit of the
 * account's own currency — `unitsPerUsd` converts a model's canonical USD cost into
 * that currency (a USD row always has `unitsPerUsd = 1.0`).
 * Dual `@ObjectType`/`@InputType` (the codebase convention, like `Order`/`Product`) so the
 * class is reusable as both the query shape and a typed GraphQL input. CRUD is served by
 * `CurrencyRateResolver` via `BaseResolver`.
 */
@ObjectType()
@InputType('CurrencyRateInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'aicurrencyrates' } })
export class CurrencyRate extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('curr') })
  declare _id: string

  /** ISO currency code (e.g. `PHP`, `USD`). Unique — see CurrencyRateSchema. */
  @Field(() => String)
  @Prop({ type: String, required: true })
  currency: string

  /** Units of this currency per 1 USD. Fluctuates over time; updated via the admin mutation. */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  unitsPerUsd: number

  @Field(() => Float)
  @Prop({ type: Number, required: true })
  markupPct: number

  /** Balance (in this currency's credits) at or below which the low-balance warning fires. */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  lowBalanceThreshold: number

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  symbol?: string

  @Field(() => Float, { nullable: true })
  @Prop({ type: Number })
  decimals?: number

  @Field(() => Boolean)
  @Prop({ type: Boolean, required: true, default: true })
  active: boolean
}
